import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useT, LanguageSwitcher } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { EnvelopeSimple } from "@phosphor-icons/react";

const BG = "https://static.prod-images.emergentagent.com/jobs/a38c17ed-f1e9-46fe-9e7c-fe4731e3e917/images/585c338ce7226b5954ccd5e96883f9bac1c70cbfa6424f609ab458b88f5c70dc.jpeg";

export default function Register() {
  const t = useT();
  const nav = useNavigate();
  const goBack = () => (window.history.state?.idx > 0 ? nav(-1) : nav("/"));
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const resend = async () => {
    try {
      await api.post("/auth/register", { name: form.name, email: form.email, password: form.password });
      toast.success(t("register.resent"));
      setCooldown(30);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/register", { name: form.name, email: form.email, password: form.password });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div className="absolute top-4 right-4 z-20"><LanguageSwitcher /></div>
      <div className="hidden md:block relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 scale-105 blur-[1px]" />
        <div className="absolute inset-0 bg-tatami-overlay" />
        <div className="absolute inset-0 bg-kukkiwon-mesh opacity-70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div>
            <button type="button" onClick={goBack} className="flex items-center gap-2 font-heading font-extrabold text-2xl tracking-tight text-foreground hover:opacity-80 transition-opacity" data-testid="register-logo-back">
              <Logo size={30} className="text-brand-yellow" /> KIHAP
            </button>
            <div className="text-muted-foreground text-xs uppercase tracking-[0.25em] mt-1 ml-9">Tournament Software for Taekwondo</div>
          </div>
          <div>
            <h1 className="font-heading text-6xl md:text-7xl font-black uppercase leading-[0.9] tracking-tight">Taekwondo<br />tournaments,<br /><span className="text-brand-yellow">simplified!</span></h1>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        {sent ? (
          <div className="w-full max-w-sm space-y-5 text-center" data-testid="register-sent">
            <div className="mx-auto w-14 h-14 rounded-full bg-brand-yellow/15 flex items-center justify-center">
              <EnvelopeSimple size={28} className="text-brand-yellow" />
            </div>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">{t("register.sentTitle")}</h2>
            <p className="text-sm text-muted-foreground">{t("register.sentMsg")} <strong className="text-foreground">{form.email}</strong></p>
            <p className="text-xs text-muted-foreground">{t("register.sentHint")}</p>
            <Button variant="outline" className="w-full" onClick={resend} disabled={cooldown > 0} data-testid="register-resend-btn">
              {cooldown > 0 ? t("register.resendWait").replace("{s}", cooldown) : t("register.resend")}
            </Button>
            <Link to="/login" className="inline-block text-sm text-white underline underline-offset-4" data-testid="register-sent-login">{t("login.enter")}</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="register-form">
            <div>
              <button type="button" onClick={goBack} className="md:hidden flex items-center gap-2 font-heading font-extrabold text-xl mb-8 text-foreground" data-testid="register-logo-back-mobile">
                <Logo size={24} className="text-brand-yellow" /> KIHAP
              </button>
              <h2 className="font-heading text-3xl font-bold uppercase tracking-tight">{t("register.title")}</h2>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-name">{t("register.name")}</Label>
              <Input id="reg-name" data-testid="register-name" value={form.name} onChange={set("name")} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-email">{t("common.email")}</Label>
              <Input id="reg-email" data-testid="register-email" type="email" value={form.email} onChange={set("email")} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-password">{t("common.password")}</Label>
              <Input id="reg-password" data-testid="register-password" type="password" value={form.password} onChange={set("password")} required />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
            <Button type="submit" className="w-full h-11 font-semibold" data-testid="register-submit" disabled={loading}>
              {loading ? t("register.creating") : t("register.cta")}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              {t("register.haveAccount")} <Link to="/login" className="text-white underline underline-offset-4">{t("register.login")}</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
